// Pure functions for transforming calendar raw events into FullCalendar events.
// Extracted from Calendar.js for testability and reuse.

export const PICKUP_COLOR = "#002FA7";
export const DELIVERY_COLOR = "#059669";
export const INV_PICKUP_COLOR = "#7C3AED";
export const INV_DELIVERY_COLOR = "#D97706";
export const RELEASE_DUE_COLOR = "#EAB308"; // giallo per "Riconsegna prevista"
export const SPAN_COLOR = "#002FA7";
export const SPAN_INV_COLOR = "#7C3AED";
export const CONFLICT_BORDER = "#DC2626";

export function eventColor(type) {
  if (type === "pickup") return PICKUP_COLOR;
  if (type === "delivery") return DELIVERY_COLOR;
  if (type === "inventory_pickup") return INV_PICKUP_COLOR;
  if (type === "inventory_delivery") return INV_DELIVERY_COLOR;
  if (type === "release_due") return RELEASE_DUE_COLOR;
  return PICKUP_COLOR;
}

export function eventTitle(ev) {
  const label = [ev.customer_name, ev.number].filter(Boolean).join(" — ");
  if (ev.type === "pickup") return `Ritiro: ${label}`;
  if (ev.type === "delivery") return `Consegna: ${label}`;
  if (ev.type === "inventory_pickup") return `Inv. Ritiro: ${label}`;
  if (ev.type === "inventory_delivery") return `Inv. Consegna: ${label}`;
  if (ev.type === "release_due") return `Riconsegna prevista: ${label}`;
  return label;
}

export function addOneDay(isoDate) {
  // FullCalendar end is exclusive for allDay events; +1 to include the end date visually.
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function clusterEventsRaw(rawEvents) {
  const perDoc = {};
  rawEvents.forEach((ev) => {
    const key = `${ev.doc_type}_${ev.doc_id}`;
    if (!perDoc[key]) perDoc[key] = { doc_type: ev.doc_type, doc_id: ev.doc_id, quote_id: ev.quote_id, items: [] };
    perDoc[key].items.push(ev);
  });
  // Cluster quote + its linked inventory(ies) into a single "work" unit
  const clusters = {};
  Object.values(perDoc).forEach((g) => {
    const key = (g.doc_type === "inventory" && g.quote_id) ? `quote_${g.quote_id}` : `${g.doc_type}_${g.doc_id}`;
    if (!clusters[key]) clusters[key] = [];
    clusters[key].push(g);
  });
  return clusters;
}

// Public export (used by tests)
export const clusterEvents = clusterEventsRaw;

function buildClusterSpan(clusterKey, groups) {
  const primary = groups.find((g) => g.doc_type === "quote") || groups[0];
  const hasInventory = groups.some((g) => g.doc_type === "inventory");
  const allDates = groups.flatMap((g) => g.items.map((i) => i.date)).filter(Boolean).sort();
  if (!allDates.length) return null;
  const start = allDates[0];
  const end = allDates[allDates.length - 1];
  const primaryItem = primary.items[0];
  const isInv = primary.doc_type === "inventory";
  const color = isInv ? SPAN_INV_COLOR : SPAN_COLOR;
  const title = [primaryItem.number, primaryItem.customer_name].filter(Boolean).join(" · ") || (isInv ? "Inventario" : "Trasloco");
  return {
    id: `span_${clusterKey}`,
    title,
    start,
    end: addOneDay(end),
    allDay: true,
    backgroundColor: color,
    borderColor: color,
    textColor: "#FFFFFF",
    editable: false,
    extendedProps: {
      ...primaryItem,
      type: "span",
      spanStart: start,
      spanEnd: end,
      hasInventory,
      clusterDocs: groups.map((g) => ({ doc_type: g.doc_type, doc_id: g.doc_id, number: g.items[0]?.number })),
    },
  };
}

export function markConflicts(spanEvents) {
  for (let i = 0; i < spanEvents.length; i++) {
    for (let j = i + 1; j < spanEvents.length; j++) {
      const a = spanEvents[i], b = spanEvents[j];
      if (a.start < b.end && b.start < a.end) {
        a.extendedProps.hasConflict = true;
        b.extendedProps.hasConflict = true;
      }
    }
  }
}

export function buildSpanEvents(rawEvents) {
  // release_due events are always rendered as standalone point events (yellow dot),
  // never clustered into "Trasloco" span bars.
  const releaseDues = rawEvents.filter((ev) => ev.type === "release_due");
  const others = rawEvents.filter((ev) => ev.type !== "release_due");
  const clusters = clusterEventsRaw(others);
  const out = [];
  Object.entries(clusters).forEach(([key, groups]) => {
    const span = buildClusterSpan(key, groups);
    if (span) out.push(span);
  });
  markConflicts(out);
  const spanEvents = out.map((e) => (
    e.extendedProps?.hasConflict
      ? { ...e, borderColor: CONFLICT_BORDER, classNames: ["calendar-conflict-event"] }
      : e
  ));
  // Add release_due as point events with yellow color
  return [...spanEvents, ...buildPointEvents(releaseDues)];
}

export function buildPointEvents(rawEvents) {
  return rawEvents.map((ev) => ({
    id: ev.id,
    title: eventTitle(ev),
    start: ev.date,
    allDay: true,
    backgroundColor: eventColor(ev.type),
    borderColor: eventColor(ev.type),
    textColor: "#FFFFFF",
    extendedProps: { ...ev, sortOrder: ev.type === "pickup" || ev.type === "inventory_pickup" ? 0 : 1 },
  }));
}
